import {addDays, differenceInSeconds} from 'date-fns';
import {match} from 'ts-pattern';

import {parseDate} from 'shared/web';

import type {
  AuctionAction,
  AuctionOffer,
  AuctionRule,
  AuctionViewTypes,
  DateTimeString,
} from './auctionView.types';

// shortcut: the BE AuctionSettings returns these defaults for each tenant. Read them from the view when the BE exposes the tenant setting.
export const DEFAULT_BIDDING_DAYS = 7;
export const DEFAULT_DECISION_DAYS = 21;

const AUCTION_ACTIONS: AuctionAction[] = [
  'publish',
  'endBidding',
  'confirmWinner',
  'relaunch',
  'abandon',
  'keepInStock',
  'openDispute',
];

// A rule that makes the action not apply to the vehicle hides the action, not only disables it (AC-23).
const HIDING_RULES: AuctionRule[] = ['NOT_TRADE_IN'];

export function auctionView(view: AuctionViewTypes) {
  return {
    actions: AUCTION_ACTIONS.map((action) => {
      const blocker = view.blockers.find(
        (candidate) => candidate.action === action && !HIDING_RULES.includes(candidate.code)
      );
      const allowed = view.allowedActions.includes(action);
      const blockerMessage = blocker?.message ?? null;
      // The UI shows an action when the BE allows it or explains why it is blocked.
      return {action, allowed, blockerMessage, isShown: allowed || blockerMessage !== null};
    }),
  };
}

// The deadlines of the round. Before the BE sets a clock, project it: a draft from today,
// a running round from its bidding end.
export function auctionDeadlines(view: AuctionViewTypes, now: Date) {
  const {biddingEndsAt, decisionDeadlineAt} = view.clocks;
  const biddingEnd = biddingEndsAt
    ? parseDate(biddingEndsAt)
    : addDays(now, view.biddingDays ?? DEFAULT_BIDDING_DAYS);
  return {
    biddingEndsAt: biddingEnd,
    decisionDeadlineAt: decisionDeadlineAt
      ? parseDate(decisionDeadlineAt)
      : addDays(biddingEnd, DEFAULT_DECISION_DAYS),
  };
}

export const AUCTION_STEPS = ['preparation', 'offers', 'confirmation'] as const;
export type AuctionStep = (typeof AUCTION_STEPS)[number];
export type AuctionStepStatus = 'done' | 'current' | 'upcoming';

// The BE creates the transaction when the seller confirms the winner and keeps it after that.
export function isAuctionAwarded(view: AuctionViewTypes) {
  return view.transaction !== null;
}

export function isOfferRejected(offer: AuctionOffer | null) {
  return offer !== null && offer.rejectedAt !== null;
}

// Amounts in different currencies are not comparable. The listing currency comes first, then the highest amount.
export function compareOfferPrice(listingCurrency: string | null) {
  const rank = (offer: AuctionOffer) => (offer.amount.currency === listingCurrency ? 0 : 1);
  return (first: AuctionOffer, second: AuctionOffer) =>
    rank(first) - rank(second) ||
    first.amount.currency.localeCompare(second.amount.currency) ||
    Number(second.amount.amount) - Number(first.amount.amount);
}

// The BE rejects a new bid in another currency (CURRENCY_MISMATCH). An older or imported offer can still have one.
export function isCurrencyMismatch(offer: AuctionOffer, listingCurrency: string | null) {
  return listingCurrency !== null && offer.amount.currency !== listingCurrency;
}

// CONTRACT art. 41, process doc 5.14: a buyer under 3 stars loses priority to the second-best bidder.
export function isBelowPriorityRating(offer: AuctionOffer) {
  return offer.traderRating !== null && offer.traderRating < 3;
}

// Process doc §10: the offers table marks the top bid. A rejected offer cannot win, so it is never the best.
export function bestOfferId(offers: AuctionOffer[], listingCurrency: string | null) {
  return (
    offers
      .filter((offer) => offer.rejectedAt === null && !isCurrencyMismatch(offer, listingCurrency))
      .reduce<AuctionOffer | null>(
        (best, offer) =>
          best === null || Number(offer.amount.amount) > Number(best.amount.amount) ? offer : best,
        null
      )?.offerId ?? null
  );
}

// The seller selects an offer to confirm it or to record a decline before the contract is signed.
// A blocked confirmation also allows the selection: the Confirmation step shows the blocker.
export function canSelectOffer(view: AuctionViewTypes) {
  return (
    !isAuctionAwarded(view) &&
    (view.allowedActions.includes('confirmWinner') ||
      view.blockers.some(({action}) => action === 'confirmWinner') ||
      view.allowedActions.includes('declineOffer'))
  );
}

// The last declined offer stays selected, so the seller sees the decline and picks another offer.
export function initialOfferId(view: AuctionViewTypes) {
  const declined = view.offers
    .filter((offer) => offer.rejectedAt !== null)
    .sort((first, second) => (second.rejectedAt ?? '').localeCompare(first.rejectedAt ?? ''));
  return view.transaction?.offerId ?? declined[0]?.offerId ?? null;
}

export type AuctionBanner =
  | 'prepare'
  | 'draftBlocked'
  | 'waiting'
  | 'offers'
  | 'decision'
  | 'confirm'
  | 'declined'
  | 'noOffers'
  | 'ended'
  | 'awaitingVehicle'
  | 'awaitingPickup'
  | 'processEnded';

// Prototype v11 "Vehicle next step": one banner for each state, not for each step.
export function auctionBanner(
  view: AuctionViewTypes,
  selectedOffer: AuctionOffer | null
): AuctionBanner | null {
  const hasOffers = view.offers.some((offer) => offer.rejectedAt === null);
  return match(view.state)
    .returnType<AuctionBanner | null>()
    .with('draft', () =>
      view.blockers.some(({action}) => action === 'publish') ? 'draftBlocked' : 'prepare'
    )
    .with('bidding', 'decision', (state) => {
      if (selectedOffer !== null) {
        return isOfferRejected(selectedOffer) ? 'declined' : 'confirm';
      }
      if (!hasOffers) {
        return state === 'bidding' ? 'waiting' : 'noOffers';
      }
      return state === 'bidding' ? 'offers' : 'decision';
    })
    .with('expired', () => (hasOffers ? 'ended' : 'noOffers'))
    .with('awaiting_vehicle', () => 'awaitingVehicle')
    .with('awaiting_pickup', () => 'awaitingPickup')
    .with('abandoned', 'kept', () => 'processEnded')
    .otherwise(() => null);
}

// The step that opens first. The prototype opens the listing again when the decision window ended.
export function auctionCurrentStep(view: AuctionViewTypes): AuctionStep {
  if (isAuctionAwarded(view)) {
    return 'confirmation';
  }
  return view.publishedAt === null || view.state === 'expired' ? 'preparation' : 'offers';
}

// selectedOfferId is the offer that the seller picked in the Offers step and did not confirm yet.
export function auctionSteps(view: AuctionViewTypes, selectedOfferId: string | null) {
  const isPublished = view.publishedAt !== null;
  const isAwarded = isAuctionAwarded(view);
  const isDone: Record<AuctionStep, boolean> = {
    preparation: isPublished && view.state !== 'expired',
    offers: isAwarded,
    confirmation: isAwarded,
  };
  const isReachable: Record<AuctionStep, boolean> = {
    preparation: true,
    offers: isPublished,
    confirmation: isAwarded || selectedOfferId !== null,
  };
  const current = auctionCurrentStep(view);

  return AUCTION_STEPS.map((step) => {
    const isCurrent = step === current || (step === 'confirmation' && isReachable.confirmation);
    const status = match({isDone: isDone[step], isCurrent})
      .returnType<AuctionStepStatus>()
      .with({isDone: true}, () => 'done')
      .with({isCurrent: true}, () => 'current')
      .otherwise(() => 'upcoming');
    return {step, status, isReachable: isReachable[step]};
  });
}

// The purchase case of the vehicle. null: the vehicle is the dealer's own stock.
export type PurchaseProgress = {hasBuyingLimit: boolean; hasBuyingPrice: boolean} | null;

export type ConfirmationCaption =
  | 'selectOffer'
  | 'selectAnotherOffer'
  | 'confirmNotify'
  | 'confirmBuyingLimit'
  | 'agreeBuyingPrice'
  | 'tradeInAgreement'
  | 'winnerConfirmed';

// The next task of the Confirmation step, in the order of prototype v11.
export function confirmationCaption(
  view: AuctionViewTypes,
  selectedOfferId: string | null,
  purchase: PurchaseProgress
): ConfirmationCaption {
  return match({
    // The handover after the award belongs to the sale business case, not to the auction (v11).
    isAwarded: isAuctionAwarded(view),
    hasSelection: selectedOfferId !== null,
    // The customer declined the buying price: the seller agrees revised terms first (AC-34).
    isRejected: isOfferRejected(
      view.offers.find(({offerId}) => offerId === selectedOfferId) ?? null
    ),
    // Own stock has no purchase business case, so it has no buying-limit step.
    isOwnStock: purchase === null,
    isPurchaseOpen:
      purchase !== null && view.blockers.some(({code}) => code === 'PURCHASE_NOT_SIGNED'),
    hasBuyingLimit: purchase?.hasBuyingLimit ?? false,
    hasBuyingPrice: purchase?.hasBuyingPrice ?? false,
  })
    .returnType<ConfirmationCaption>()
    .with({isAwarded: true}, () => 'winnerConfirmed')
    .with({hasSelection: false}, () => 'selectOffer')
    .with({isRejected: true, isOwnStock: true}, () => 'selectAnotherOffer')
    .with({isRejected: true}, () => 'confirmBuyingLimit')
    .with({isPurchaseOpen: false}, () => 'confirmNotify')
    .with({hasBuyingLimit: false}, () => 'confirmBuyingLimit')
    .with({hasBuyingPrice: true}, () => 'tradeInAgreement')
    .with({hasBuyingPrice: false}, () => 'agreeBuyingPrice')
    .exhaustive();
}

export type ClockTone = 'ok' | 'soon' | 'overdue';

const HOUR_SECONDS = 60 * 60;
const DAY_SECONDS = 24 * HOUR_SECONDS;
// A countdown longer than this is noise; the UI shows only the date.
const FAR_SECONDS = 30 * DAY_SECONDS;

// Trade owns the deadlines. Green: more than 24 h. Amber: less than 24 h. Red: overdue.
export function auctionClock(deadline: DateTimeString, now: Date) {
  const remainingSeconds = differenceInSeconds(parseDate(deadline), now);
  const absoluteSeconds = Math.abs(remainingSeconds);
  const tone = match(remainingSeconds)
    .returnType<ClockTone>()
    .when(
      (seconds) => seconds < 0,
      () => 'overdue'
    )
    .when(
      (seconds) => seconds < DAY_SECONDS,
      () => 'soon'
    )
    .otherwise(() => 'ok');

  return {
    tone,
    isFar: remainingSeconds > FAR_SECONDS,
    days: Math.floor(absoluteSeconds / DAY_SECONDS),
    hours: Math.floor((absoluteSeconds % DAY_SECONDS) / HOUR_SECONDS),
  };
}
